/**
 * Resource Preloading
 * 
 * Preload critical resources to improve performance.
 * - Fonts
 * - Critical images
 * - API data
 * - Routes
 */

/**
 * Preload font files
 */
export function preloadFonts(fonts: string[]): void {
  fonts.forEach((font) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.href = font;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
}

/**
 * Preload image
 */
export function preloadImage(src: string, priority: 'high' | 'low' = 'low'): Promise<void> {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    if (priority === 'high') {
      link.setAttribute('fetchpriority', 'high');
    }
    
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to preload image: ${src}`));
    
    document.head.appendChild(link);
  });
}

/**
 * Preload multiple images
 */
export async function preloadImages(images: Array<{ src: string; priority?: 'high' | 'low' }>): Promise<void> {
  await Promise.all(
    images.map((img) => preloadImage(img.src, img.priority))
  );
}

/**
 * Preload script
 */
export function preloadScript(src: string): void {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = src;
  document.head.appendChild(link);
}

/**
 * Preload route/component
 * 
 * Triggers dynamic import for lazy-loaded routes
 */
export async function preloadRoute(importFn: () => Promise<any>): Promise<void> {
  try {
    await importFn();
  } catch (error) {
    console.error('[Preload] Failed to preload route:', error);
  }
}

/**
 * Prefetch API data
 * 
 * Warm up React Query cache with likely-needed data
 */
export async function prefetchData<T>(
  queryKey: any[],
  queryFn: () => Promise<T>,
  queryClient: any
): Promise<void> {
  try {
    await queryClient.prefetchQuery({
      queryKey,
      queryFn,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  } catch (error) {
    console.error('[Preload] Failed to prefetch data:', error);
  }
}

/**
 * Preload critical resources on app load
 */
export function preloadCriticalResources(): void {
  // Fonts (if using custom fonts)
  // preloadFonts([
  //   '/fonts/inter-var.woff2',
  // ]);
  
  // Critical images
  // preloadImages([
  //   { src: '/images/logo.svg', priority: 'high' },
  // ]);
  
  console.log('[Preload] Critical resources loaded');
}

/**
 * Preload likely next routes based on user behavior
 * 
 * @example
 * ```tsx
 * // When user visits dashboard, preload transactions
 * if (currentPath === '/dashboard') {
 *   preloadRoute(() => import('./features/transactions'));
 * }
 * ```
 */
export function preloadLikelyRoutes(currentPath: string): void {
  // Use requestIdleCallback to avoid blocking
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      switch (currentPath) {
        case '/dashboard':
          // User likely to go to transactions next
          import('../features/transactions');
          break;
        case '/entries':
          // User likely to create transaction
          import('../features/transactions');
          break;
        case '/reports':
          // User might go to budget
          import('../features/budgets/BudgetPageNew');
          break;
        default:
          break;
      }
    }, { timeout: 2000 });
  }
}

/**
 * DNS prefetch for external domains
 */
export function dnsPrefetch(domains: string[]): void {
  domains.forEach((domain) => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = domain;
    document.head.appendChild(link);
  });
}

/**
 * Preconnect to critical origins
 */
export function preconnect(origins: string[]): void {
  origins.forEach((origin) => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
}

/**
 * Initialize all preloading strategies
 */
export function initializePreloading(config: {
  supabaseUrl?: string;
  cdnUrl?: string;
}): void {
  // DNS prefetch for Supabase
  if (config.supabaseUrl) {
    dnsPrefetch([config.supabaseUrl]);
    preconnect([config.supabaseUrl]);
  }
  
  // DNS prefetch for CDN
  if (config.cdnUrl) {
    dnsPrefetch([config.cdnUrl]);
  }
  
  // Preload critical resources
  preloadCriticalResources();
  
  console.log('[Preload] Initialization complete');
}

